import { isEmpty, compact, remove } from 'lodash';
import { type AxiosResponseHeaders } from 'axios';
import { stats } from '@fiverr-private/obs';
import { Brief, BriefSection, DynamicSection, SectionTypes } from '@fiverr-private/ai_brief';
import { getContext } from '@fiverr-private/fiverr_context';
import { Gql } from '@fiverr-private/dynamic_matching';
import { MAX_EXTRACTION_INPUT_LENGTH, METRICS_PREFIX, NOT_SURE_METADATA_KEY, Step } from '../constants';
import { Rollouts, useBriefCreationContext } from '../context/BriefCreationContext';
import {
    LeafClassification,
    MetadataAnswersType,
    submitBrief,
    SubmitBriefRequestData,
    ExperimentAllocation,
    initSellersSearch,
} from '../api';
import {
    BriefNeedType,
    BudgetSectionContent,
    GENERATED_CONTENT_EXCLUDED_SECTION_IDS,
    MANDATORY_GENERATED_SECTION_TITLES,
    MetadataSectionContent,
    NEED_TYPE_SECTION_ID,
    ScreeningQuestionsContent,
    TimelineContentType,
    screeningQuestionsSectionID,
} from '../components';
import {
    BudgetAndTimelineContent,
    mapTimeUnitsToApiTimeUnit,
} from '../components/Sections/BudgetAndTimeline/utils/types';
import { BUDGET_AND_TIMELINE_SECTION_ID } from '../components/Sections/BudgetAndTimeline';
import { briefCreationClickAction, mixpanelEvents, reportTimeMetric } from '../monitoring';
import { getCentsInCurrency } from '../components/Sections/Budget/formatCurrency';
import { logger } from '../utils/logger';
import { IdealCandidatePreferences } from '../components/Sections/IdealCandidate/utils/types';
import { snakeCaseFreelancerPreferences } from '../components/Sections/IdealCandidate/utils/utils';
import { idealCandidateSectionID } from '../components/Sections/IdealCandidate';
import { Currency } from '../types/currency';
import { BriefsMobileBridge } from '../events/BriefsMobileBridge';
import { useGetSearchParametersData } from './classification/useGetSearchParametersData';
import { useGetCurrentFlow } from './useGetCurrentFlow';

const CROWS_AUCTION_INVITE_FLOW_EXPERIMENT = 4999;
const BRIEF_EXPERIMENT_ALLOCATIONS = 'x-experiment-allocations';

const isInCrowsAuctionInviteFlowExperiment = ({
    headers,
    flow,
}: {
    headers: AxiosResponseHeaders;
    flow: Gql.DmMatchCandidateCreationFlow | null;
}): boolean => {
    const { abTests } = getContext();
    const alreadyAllocatedGroup = Number(abTests[CROWS_AUCTION_INVITE_FLOW_EXPERIMENT]);

    if (alreadyAllocatedGroup) {
        const localCondition = flow === Gql.DmMatchCandidateCreationFlow.Auction;
        return alreadyAllocatedGroup > 1 && localCondition;
    }

    // Checks allocation from the server (includes local conditions)
    const experimentAllocations = headers[BRIEF_EXPERIMENT_ALLOCATIONS];

    return (
        !!experimentAllocations &&
        experimentAllocations.split(',').includes(`${CROWS_AUCTION_INVITE_FLOW_EXPERIMENT}=2`)
    );
};

export const getNeedTypeData = (brief: Brief<any>, enableEnhancedUx: boolean) => {
    const needTypeSection = brief.sections.find((section) => section.id === NEED_TYPE_SECTION_ID && section.included);
    const needTypeContent = (needTypeSection?.content ?? {}) as BriefNeedType;
    const { needType } = needTypeContent;

    const defaultEnrichment = {
        need_type: needType,
    };

    if (needType !== Gql.DmBriefNeedType.OneTime) {
        return defaultEnrichment;
    }

    const sectionWithPotential = enableEnhancedUx
        ? brief.sections.find((section) => section.id === BUDGET_AND_TIMELINE_SECTION_ID && section.included)
        : needTypeSection;

    return {
        ...defaultEnrichment,
        need_type_details: {
            ongoing_work_potential: Boolean(sectionWithPotential?.content?.ongoingWorkPotential),
        },
    };
};

const getMetadataAnswers = (sections: BriefSection[]) => {
    // exclude mandatory metadata answers that are not sure
    const metadataAnswersSections = sections.filter(
        (section) =>
            section.type === SectionTypes.metadata && section.included && section.content[0] !== NOT_SURE_METADATA_KEY
    );

    return metadataAnswersSections.reduce((acc: Record<string, string[]>, section) => {
        const metadataContent: MetadataSectionContent = (section as any).metadata;
        acc[metadataContent.question.metadata_type] = section.content as unknown as string[];
        return acc;
    }, {});
};

const getScreeningQuestions = (
    sections: BriefSection<ScreeningQuestionsContent>[],
    enableScreeningQuestions: boolean
): SubmitBriefRequestData['screening_questions'] | null => {
    if (!enableScreeningQuestions) {
        return null;
    }

    const screeningQuestionsSection = sections.find(
        (section) => section.id === screeningQuestionsSectionID && section.included
    );

    if (!screeningQuestionsSection) {
        return null;
    }

    return screeningQuestionsSection.content.questions.map((question) => ({
        kind: question.suggestedQuestionId
            ? Gql.DmBriefScreeningQuestionKind.Suggested
            : Gql.DmBriefScreeningQuestionKind.Custom,
        question: question.text,
    }));
};

const aggregateBriefToString = (sections: BriefSection[]): string => {
    const contentToString = (section: BriefSection): string => {
        const { id, content, title } = section;
        const normalizedTitle = title?.toLowerCase()?.replace(' ', '_');

        if (id === idealCandidateSectionID) {
            const idealCandidateContent = content as IdealCandidatePreferences;

            return idealCandidateContent?.skillsAndExperience ?? '';
        } else if (isEmpty(content) || normalizedTitle === 'company_details') {
            return '';
        } else if (typeof content === 'string') {
            return content;
        } else {
            return JSON.stringify(content);
        }
    };

    const shouldIncludeSection = (section: BriefSection): boolean => {
        const isTextualSection = section.type === SectionTypes.shortText || section.type === SectionTypes.generated;
        const isIdealCandidateSection = section.id === idealCandidateSectionID;

        return section.included && (isTextualSection || isIdealCandidateSection) && !isEmpty(contentToString(section));
    };

    return sections
        .filter((section) => shouldIncludeSection(section))
        .map((section) => `${section.title}:\n${contentToString(section)}\n\n`)
        .join('')
        .slice(0, MAX_EXTRACTION_INPUT_LENGTH);
};

interface SubmitBriefInputParams
    extends Pick<
        ReturnType<typeof useBriefCreationContext>,
        'contextId' | 'userInput' | 'briefClassification' | 'currency' | 'buyerType'
    > {
    brief: Brief;
    searchQuery?: string;
    relaxedMetadataAnswers?: MetadataAnswersType;
    preferredRncFlow?: Gql.DmBriefTier;
    summary?: string;
    experimentAllocation?: ExperimentAllocation;
    rollouts: Rollouts['rollouts'];
    enableEnhancedUx: boolean;
    enableScreeningQuestions: boolean;
}

const extractBudgetAndTimeline = (
    brief: Brief,
    currency: Currency,
    rollouts: Rollouts['rollouts']
): Partial<SubmitBriefRequestData> => {
    const inMustangsPersonBrief = !!rollouts?.inMustangsPersonBrief;

    if (inMustangsPersonBrief) {
        const budgetAndTimelineSection = brief.sections.find(
            (section) => section.id === 'budget_and_timeline'
        ) as DynamicSection;
        const budgetAndTimelineContent = budgetAndTimelineSection?.content as BudgetAndTimelineContent;

        const { budgetInCents } = budgetAndTimelineContent ?? {};
        const hourlyRange = budgetAndTimelineContent?.hourlyBudgetRange;
        const { timelineDetails } = budgetAndTimelineContent;
        const fallbackMaxBudget: number | undefined = hourlyRange?.max ?? budgetInCents;

        if (!fallbackMaxBudget) {
            const errorMessage = 'Invalid state, both hourlyRange and budgetInCents are undefined';
            logger.error(errorMessage);
            throw new Error(errorMessage);
        }

        return {
            ...(budgetInCents
                ? {
                      budget_in_cents: budgetInCents,
                      original_buyer_budget: {
                          amount: getCentsInCurrency(budgetInCents, currency) * 100,
                          currency: currency.name,
                      },
                  }
                : {}),
            ...(!isEmpty(hourlyRange)
                ? {
                      budget_range_in_cents: hourlyRange,
                  }
                : {}),
            original_buyer_budget_range: {
                max: {
                    amount: getCentsInCurrency(fallbackMaxBudget, currency),
                    currency: currency.name,
                },
                ...(hourlyRange?.min
                    ? {
                          min: {
                              amount: getCentsInCurrency(hourlyRange.min, currency),
                              currency: currency.name,
                          },
                      }
                    : {}),
            },
            delivery_date_epoch_ms: budgetAndTimelineContent?.deliveryDate,
            pricing_type: budgetAndTimelineContent?.pricingType,
            timeline: budgetAndTimelineContent?.timeline,
            ...(!isEmpty(timelineDetails)
                ? {
                      timeline_details: {
                          ...(!isEmpty(timelineDetails.generalTimeline)
                              ? {
                                    general_timeline: {
                                        ...(timelineDetails.generalTimeline.unit && {
                                            unit: mapTimeUnitsToApiTimeUnit(timelineDetails.generalTimeline.unit),
                                        }),
                                        ...(timelineDetails.generalTimeline.value && {
                                            value: timelineDetails.generalTimeline.value,
                                        }),
                                    },
                                }
                              : {}),
                          ...(!isEmpty(timelineDetails.timeCommitment)
                              ? { time_commitment: timelineDetails.timeCommitment?.toUpperCase() }
                              : {}),
                          ...(!isEmpty(timelineDetails.hiringDuration)
                              ? { hiring_duration: timelineDetails.hiringDuration?.toUpperCase() }
                              : {}),
                          ...(!isEmpty(timelineDetails.deliveriesFrequency)
                              ? {
                                    deliveries_frequency: {
                                        ...(timelineDetails.deliveriesFrequency.unit && {
                                            unit: mapTimeUnitsToApiTimeUnit(timelineDetails.deliveriesFrequency.unit),
                                        }),
                                        ...(timelineDetails.deliveriesFrequency.value && {
                                            value: timelineDetails.deliveriesFrequency.value,
                                        }),
                                    },
                                }
                              : {}),
                      },
                  }
                : {}),
        };
    } else {
        const budgetSection = brief.sections.find((section) => section.id === 'budget') as DynamicSection;
        const budgetContent = budgetSection?.content as BudgetSectionContent;

        const timelineSection = brief.sections.find((section) => section.id === 'timeline');
        const timelineSectionContent = timelineSection?.content as TimelineContentType;

        const { budgetInCents, pricingType } = budgetContent;
        const { deliveryDate, timeline } = timelineSectionContent;

        return {
            budget_in_cents: budgetInCents,
            delivery_date_epoch_ms: deliveryDate,
            pricing_type: pricingType,
            timeline,
            original_buyer_budget: {
                amount: getCentsInCurrency(budgetInCents, currency) * 100,
                currency: currency.name,
            },
        };
    }
};

const mapBriefToSubmitRequestData = ({
    brief,
    userInput,
    briefClassification,
    contextId,
    searchQuery,
    currency,
    relaxedMetadataAnswers,
    preferredRncFlow,
    summary,
    experimentAllocation,
    rollouts,
    buyerType,
    enableEnhancedUx,
    enableScreeningQuestions,
}: SubmitBriefInputParams): SubmitBriefRequestData => {
    const briefSections = (brief.sections as DynamicSection[]).filter(
        (section) => !isEmpty(section.content) && section.included
    );

    const budgetAndTimeline = extractBudgetAndTimeline(brief, currency, rollouts);

    const metadataAnswers = getMetadataAnswers(briefSections);

    const [idealCandidateSection] = remove(briefSections, (section) => section.id === idealCandidateSectionID);
    const { skillsAndExperience, ...freelancerPreferences } =
        idealCandidateSection?.content ?? ({} as IdealCandidatePreferences);
    const nonMandatorySections = briefSections.filter(
        (section) =>
            !section.mandatory &&
            section.included &&
            section.type !== SectionTypes.metadata &&
            !GENERATED_CONTENT_EXCLUDED_SECTION_IDS.includes(section.id)
    );

    if (skillsAndExperience) {
        nonMandatorySections.push({
            title: 'Ideal Candidate',
            content: skillsAndExperience,
            included: true,
        } as DynamicSection);
    }

    const mandatoryGeneratedSections = briefSections.filter(
        (section) =>
            section.title &&
            MANDATORY_GENERATED_SECTION_TITLES.includes(section.title) &&
            section.type === SectionTypes.generated
    );

    const attachmentSection = brief.sections.find((section) => section.type === SectionTypes.attachments);
    const titleSection = brief.sections.find((section) => section.id === 'title');
    const title = titleSection?.content || brief.title;

    const screeningQuestions = getScreeningQuestions(briefSections, enableScreeningQuestions);

    let briefRequestData: SubmitBriefRequestData = {
        metadata_answers: {
            service_options: metadataAnswers,
            ...(!isEmpty(freelancerPreferences)
                ? { freelancer_preferences: snakeCaseFreelancerPreferences(freelancerPreferences) }
                : {}),
        },
        relaxed_metadata_answers: relaxedMetadataAnswers,
        title,
        description: userInput,
        category_id: briefClassification!.leaf_classification[0].category_id,
        sub_category_id: briefClassification!.leaf_classification[0].sub_category_id,
        nested_sub_category_id: briefClassification!.leaf_classification[0].nested_sub_category_id || null,
        context_id: contextId,
        content: {
            generated_content: [...mandatoryGeneratedSections, ...nonMandatorySections].map(
                ({ id, title, content }) => ({
                    id,
                    title,
                    content,
                })
            ),
        },
        ...budgetAndTimeline,
        search_query: searchQuery,
        summary,
        attachment_ids: attachmentSection?.included
            ? compact(attachmentSection?.attachments?.map((attachment) => attachment.pending_attachment_id))
            : [],
        ...(buyerType ? { buyer_type: buyerType } : {}),
        ...(rollouts?.inMustangsPersonBrief ? getNeedTypeData(brief, enableEnhancedUx) : {}),
        ...(experimentAllocation
            ? { experiment_allocations: { [experimentAllocation.experiment_id]: experimentAllocation } }
            : {}),
        ...(screeningQuestions ? { screening_questions: screeningQuestions } : {}),
    };

    if (preferredRncFlow) {
        briefRequestData = {
            ...briefRequestData,
            preferred_tier: preferredRncFlow,
        };
    }

    return briefRequestData;
};

export const useSubmitBrief = () => {
    const {
        brief,
        briefClassification,
        userInput,
        contextId,
        missingFieldsCount,
        setIsBriefLoading,
        isBriefLoading,
        source,
        onSuccess,
        currency,
        rollouts,
        variant,
        parentElement,
        buyerType,
        setIsAllocatedAuctionInviteFlow,
        enableEnhancedUx,
        enableScreeningQuestions,
    } = useBriefCreationContext();
    const { getSearchParametersData } = useGetSearchParametersData();
    const flow = useGetCurrentFlow();
    const abortController = new AbortController();

    const handleSubmitError = (error?: Error) => {
        logger.error(new Error('Failed while classifying user input to category'), { cause: error });
        stats.count(`${METRICS_PREFIX}.submit_brief`, `error`);

        BriefsMobileBridge.onError({
            type: 'BRIEF_SUBMISSION_FAILED',
        });
    };

    const handleSubmit = async (preferredRncFlow?: Gql.DmBriefTier): Promise<string | undefined> => {
        if (!brief) {
            handleSubmitError(new Error('Cannot submit brief with missing mandatory fields'));
            return;
        }

        if (missingFieldsCount) {
            handleSubmitError(new Error('Cannot submit brief with missing mandatory fields'));
            return;
        }

        briefCreationClickAction({
            step: Step.Finish,
            action: preferredRncFlow === Gql.DmBriefTier.Auction ? 'source_for_me' : 'post_brief',
            briefContextId: contextId,
            eventSource: source || '',
            parentElement,
            variant,
        });

        setIsBriefLoading(true);
        const submissionStartTime = Date.now();
        try {
            const leafClassification = briefClassification?.leaf_classification[0] as LeafClassification;
            const { budget_in_cents, pricing_type, budget_range_in_cents } = extractBudgetAndTimeline(
                brief,
                currency,
                rollouts
            );

            const searchData = await getSearchParametersData({
                leafClassification,
                userInput: aggregateBriefToString(brief.sections),
                contextId,
                abortController,
                metadata: {
                    filters: getMetadataAnswers(brief.sections),
                },
                tierData: {
                    budget: budget_in_cents ?? budget_range_in_cents?.max,
                    pricing_type,
                    preferred_flow: preferredRncFlow,
                },
            });

            const { searchQuery, metadata, summary, experimentAllocation } = searchData || {};

            const briefWithDescription = brief;
            const isDuplicateFlow = !userInput && !!briefWithDescription.description;
            const descriptionForSubmit: string = isDuplicateFlow ? briefWithDescription.description! : userInput;

            const briefRequestData = mapBriefToSubmitRequestData({
                brief,
                userInput: descriptionForSubmit,
                briefClassification,
                contextId,
                searchQuery,
                currency,
                relaxedMetadataAnswers: metadata,
                preferredRncFlow,
                summary,
                experimentAllocation,
                rollouts,
                buyerType,
                enableEnhancedUx,
                enableScreeningQuestions,
            });

            const { data: briefId, headers } = await submitBrief(briefRequestData);

            if (!briefId) {
                handleSubmitError();
                return;
            }
            if (summary) {
                await initSellersSearch({ briefId });
            }

            if (briefId) {
                BriefsMobileBridge.onBriefCreationDone({ briefId, flow });
            }

            const isInAuctionInviteFlow = isInCrowsAuctionInviteFlowExperiment({ headers, flow });

            setIsAllocatedAuctionInviteFlow(isInAuctionInviteFlow);

            onSuccess?.();
            stats.count(`${METRICS_PREFIX}.submit_brief`, `success`);
            if (isDuplicateFlow) {
                mixpanelEvents.seamless(variant).duplicateBrief.success();
            }
            return briefId;
        } catch (error) {
            handleSubmitError(error as Error);
        } finally {
            setIsBriefLoading(false);
            reportTimeMetric({
                metricName: 'submit_brief',
                time: Date.now() - submissionStartTime,
            });
        }
    };

    return {
        isBriefLoading,
        handleSubmit,
    };
};
